import { moment } from '@hotjar/insights-common/lib/moment';
import type {
  AbsoluteDateRange,
  AllDateRange,
  DateRange,
  DATE_UNITS,
  MinimalDateConstraint,
  RelativeDateRange,
} from './DateRangeFilterType';
import { DATE_RANGE_TYPE } from './DateRangeFilterType';

export function makeAbsoluteDateRange(from: Date, to: Date): AbsoluteDateRange {
  return {
    type: DATE_RANGE_TYPE.ABSOLUTE,
    from,
    to,
  };
}

export function makeRelativeDateRange(amount: number, unit: DATE_UNITS): RelativeDateRange {
  return {
    type: DATE_RANGE_TYPE.RELATIVE,
    unit,
    amount,
  };
}

export function makeAllDateRange(): AllDateRange {
  return {
    type: DATE_RANGE_TYPE.ALL,
  };
}

export function equalsDateRange(dateRange?: DateRange, otherDateRange?: DateRange): boolean {
  if (!dateRange || !otherDateRange) {
    return false;
  }
  const dateRangeType = dateRange.type;
  switch (dateRange.type) {
    case DATE_RANGE_TYPE.RELATIVE:
      return (
        otherDateRange.type === DATE_RANGE_TYPE.RELATIVE &&
        otherDateRange.unit === dateRange.unit &&
        otherDateRange.amount === dateRange.amount
      );
    case DATE_RANGE_TYPE.ABSOLUTE:
      return (
        otherDateRange.type === DATE_RANGE_TYPE.ABSOLUTE &&
        otherDateRange.from === dateRange.from &&
        otherDateRange.to === dateRange.to
      );
    case DATE_RANGE_TYPE.ALL:
      return otherDateRange.type === DATE_RANGE_TYPE.ALL;
    default:
      throw new Error(`Unknown DateRange type: ${dateRangeType}`);
  }
}

export const isDateRangeBeforeMinDate = (
  dateRange: DateRange,
  dateConstraint?: MinimalDateConstraint,
): boolean => {
  if (!dateConstraint) {
    return false;
  }
  const dateRangeType = dateRange.type;
  switch (dateRange.type) {
    case DATE_RANGE_TYPE.RELATIVE:
      return moment().subtract(dateRange.amount, dateRange.unit).isBefore(dateConstraint.minDate);
    case DATE_RANGE_TYPE.ABSOLUTE:
      return moment(dateRange.from).isBefore(dateConstraint.minDate);
    case DATE_RANGE_TYPE.ALL:
      return true;
    default:
      throw new Error(`Unknown DateRange type: ${dateRangeType}`);
  }
};

export const getMinDateMessage = (
  range: RelativeDateRange,
  dateConstraint?: MinimalDateConstraint,
): string | undefined => {
  if (dateConstraint && isDateRangeBeforeMinDate(range, dateConstraint)) {
    return dateConstraint.reason;
  }

  return undefined;
};

const generateToDate = (dateRange: DateRange, dateConstraint: MinimalDateConstraint): Date => {
  const dateRangeType = dateRange.type;
  switch (dateRange.type) {
    case DATE_RANGE_TYPE.RELATIVE:
      return new Date();
    case DATE_RANGE_TYPE.ABSOLUTE:
      return moment(dateRange.to).isBefore(dateConstraint.minDate)
        ? dateConstraint.minDate
        : dateRange.to;
    case DATE_RANGE_TYPE.ALL:
      return new Date();
    default:
      throw new Error(`Unknown DateRange type: ${dateRangeType}`);
  }
};

export const applyDateConstraint = (
  dateRange: DateRange,
  dateConstraint?: MinimalDateConstraint,
): DateRange => {
  if (!dateConstraint || !isDateRangeBeforeMinDate(dateRange, dateConstraint)) {
    return dateRange;
  }

  return {
    type: DATE_RANGE_TYPE.ABSOLUTE,
    from: dateConstraint.minDate,
    to: generateToDate(dateRange, dateConstraint),
    autoSelectedConstraint: dateConstraint,
  };
};

export const convertToAbsoluteDateRange = (dateRange: DateRange): AbsoluteDateRange => {
  const dateRangeType = dateRange.type;
  const now = new Date();
  switch (dateRange.type) {
    case DATE_RANGE_TYPE.RELATIVE:
      return {
        type: DATE_RANGE_TYPE.ABSOLUTE,
        from: moment().subtract(dateRange.amount, dateRange.unit).toDate(),
        to: now,
      };
    case DATE_RANGE_TYPE.ABSOLUTE:
      return dateRange;
    case DATE_RANGE_TYPE.ALL:
      return {
        type: DATE_RANGE_TYPE.ABSOLUTE,
        from: new Date(0), // <- 1970
        to: now,
      };
    default:
      throw new Error(`Unknown DateRange type: ${dateRangeType}`);
  }
};
